import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, X, Tag } from "lucide-react";
import { User } from "@supabase/supabase-js";

interface AttachmentTag {
  id: string;
  tag: string;
  created_by: string;
}

interface AttachmentTagsProps {
  attachmentId: string;
  user: User;
}

export const AttachmentTags = ({ attachmentId, user }: AttachmentTagsProps) => {
  const [tags, setTags] = useState<AttachmentTag[]>([]);
  const [newTag, setNewTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchTags();
  }, [attachmentId]);

  // Real-time subscription for tag changes
  useEffect(() => {
    const channel = supabase
      .channel(`attachment-tags-${attachmentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'attachment_tags',
          filter: `attachment_id=eq.${attachmentId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setTags(prev => [...prev, payload.new as AttachmentTag]);
          } else if (payload.eventType === 'DELETE') {
            setTags(prev => prev.filter(t => t.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [attachmentId]);

  const fetchTags = async () => {
    try {
      const { data, error } = await supabase
        .from("attachment_tags")
        .select("*")
        .eq("attachment_id", attachmentId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setTags(data || []);
    } catch (error: any) {
      console.error("Error fetching tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async () => {
    const trimmedTag = newTag.trim().toLowerCase();
    
    if (!trimmedTag) {
      toast.error("Please enter a tag");
      return;
    }

    if (tags.some(t => t.tag === trimmedTag)) {
      toast.error("Tag already exists");
      return;
    }

    setAdding(true);
    try {
      const { error } = await supabase
        .from("attachment_tags")
        .insert({
          attachment_id: attachmentId,
          tag: trimmedTag,
          created_by: user.id
        });

      if (error) throw error;

      setNewTag("");
      toast.success("Tag added");
    } catch (error: any) {
      console.error("Error adding tag:", error);
      toast.error("Failed to add tag");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const { error } = await supabase
        .from("attachment_tags")
        .delete()
        .eq("id", tagId);

      if (error) throw error;
      toast.success("Tag removed");
    } catch (error: any) {
      console.error("Error removing tag:", error);
      toast.error("Failed to remove tag");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Tags</span>
      </div>

      {/* Tag Input */}
      <div className="flex gap-2">
        <Input
          placeholder="Add tag..."
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1"
          disabled={adding}
        />
        <Button
          size="sm"
          onClick={handleAddTag}
          disabled={adding || !newTag.trim()}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {/* Tags Display */}
      <div className="flex flex-wrap gap-2">
        {loading ? (
          <span className="text-xs text-muted-foreground">Loading tags...</span>
        ) : tags.length === 0 ? (
          <span className="text-xs text-muted-foreground">No tags yet</span>
        ) : (
          tags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="gap-1">
              {tag.tag}
              {tag.created_by === user.id && (
                <button
                  onClick={() => handleRemoveTag(tag.id)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))
        )}
      </div>
    </div>
  );
};
